import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { fetchDeployedVersion, isOutdated, movedOn } from './deployedVersion';

/*
 * Tells the app when the server has moved on to a newer release.
 *
 * Nothing here reloads on its own: an automatic reload would throw
 * away whatever the user is typing. The hint can be dismissed, and a
 * marker next to the footer version keeps it reachable afterwards.
 *
 * The state is deliberately kept in memory only. A flag in
 * localStorage would survive the reload that resolves it — and then
 * announce a "new version" to a tab that is already running it.
 */

/* Long enough not to matter, short enough that someone who leaves the
   tab open over lunch is told before they navigate somewhere new. The
   checks on coming back to the app are the ones that usually fire
   first. */
const CHECK_INTERVAL_MS = 15 * 60 * 1000;

/* one return to the app, not two: a tab switch fires visibilitychange
   and focus back to back, and both mean the same arrival */
const SAME_RETURN_MS = 1000;

const VersionUpdateContext = createContext({
  updateAvailable: false,
  deployed: null,
  dismissed: false,
  dismiss: () => {},
  reveal: () => {},
});

export function VersionUpdateProvider({ children }) {
  const [deployed, setDeployed] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  /* the release the first answer of this page load named — as close as
     this tab can get to knowing which release it was served from, and
     the only way to notice a redeploy of an unchanged commit */
  const loadedRelease = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const check = async () => {
      const version = await fetchDeployedVersion(controller.signal);
      if (cancelled || !version) return;

      const known = loadedRelease.current;
      if (known === null) loadedRelease.current = version.release ?? '';

      if (isOutdated(version) || movedOn(version, known)) setDeployed(version);
    };

    check();
    const timer = setInterval(check, CHECK_INTERVAL_MS);

    /* Coming back to the app is the moment a stale tab is most likely
       to be about to navigate somewhere it cannot load. Two events say
       so, and neither covers the other: switching tabs or minimising
       hides the page and fires `visibilitychange`, while merely
       clicking into another window leaves the page visible — the
       deploy in the terminal beside the browser is exactly that case,
       and only `focus` reports it. */
    let lastReturn = 0;
    const onReturn = () => {
      if (document.hidden) return;
      /* a tab switch fires both events, one after the other; asking
         twice in the same instant would answer the same thing */
      const now = Date.now();
      if (now - lastReturn < SAME_RETURN_MS) return;
      lastReturn = now;
      check();
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);

    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, []);

  const dismiss = useCallback(() => setDismissed(true), []);
  const reveal = useCallback(() => setDismissed(false), []);

  return (
    <VersionUpdateContext.Provider
      value={{
        updateAvailable: deployed !== null,
        deployed,
        dismissed,
        dismiss,
        reveal,
      }}
    >
      {children}
    </VersionUpdateContext.Provider>
  );
}

export function useVersionUpdate() {
  return useContext(VersionUpdateContext);
}
